/**
 * materials.ts — 재료 드랍 시스템 (Sprint 1)
 *
 * 재료 4종: iron_dust(Lv1-9), refining_core(Lv10-19),
 *           lava_essence(Lv20-24), abyss_crystal(Lv25-34)
 *
 * 브라우저 API를 직접 사용하지 않는 순수 로직 + 아이콘 경로만 제공.
 * localStorage 접근은 main.ts의 storage 객체를 통해 위임.
 */

export type MaterialKey = 'iron_dust' | 'refining_core' | 'lava_essence' | 'abyss_crystal';

export interface MaterialDef {
    key: MaterialKey;
    /** 해당 재료가 드랍되는 레벨 범위 (inclusive) */
    levelMin: number;
    levelMax: number;
    /** public/items/ 내 PNG 파일명 */
    icon: string;
    /** 실패 시 드랍 범위 [min, max] */
    dropOnFail: [number, number];
    /** 파괴 시 드랍 범위 [min, max] */
    dropOnDestroy: [number, number];
}

export interface Materials {
    iron_dust: number;
    refining_core: number;
    lava_essence: number;
    abyss_crystal: number;
}

export const MATERIAL_DEFS: MaterialDef[] = [
    {
        key: 'iron_dust',
        levelMin: 1,
        levelMax: 9,
        icon: './items/mat_iron_dust.png',
        dropOnFail: [1, 2],
        dropOnDestroy: [2, 4],
    },
    {
        key: 'refining_core',
        levelMin: 10,
        levelMax: 19,
        icon: './items/mat_refining_core.png',
        dropOnFail: [1, 2],
        dropOnDestroy: [2, 4],
    },
    {
        key: 'lava_essence',
        levelMin: 20,
        levelMax: 24,
        icon: './items/mat_lava_essence.png',
        dropOnFail: [1, 2],
        dropOnDestroy: [2, 4],
    },
    {
        key: 'abyss_crystal',
        levelMin: 25,
        levelMax: 34,
        icon: './items/mat_abyss_crystal.png',
        dropOnFail: [1, 1],
        dropOnDestroy: [2, 3],
    },
];

export function emptyMaterials(): Materials {
    return { iron_dust: 0, refining_core: 0, lava_essence: 0, abyss_crystal: 0 };
}

/**
 * 주어진 레벨대에 해당하는 MaterialDef를 반환. 없으면 null.
 */
export function getMaterialDefForLevel(level: number): MaterialDef | null {
    return MATERIAL_DEFS.find((d) => level >= d.levelMin && level <= d.levelMax) ?? null;
}

function randInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export interface DropResult {
    key: MaterialKey;
    amount: number;
}

/**
 * 강화 실패 시 재료 드랍 계산.
 * @param level 강화 시도한 레벨
 * @returns 드랍 결과 (드랍 없으면 null)
 */
export function calcFailDrop(level: number): DropResult | null {
    const def = getMaterialDefForLevel(level);
    if (!def) return null;
    const amount = randInt(def.dropOnFail[0], def.dropOnFail[1]);
    return { key: def.key, amount };
}

/**
 * 파괴 시 재료 드랍 계산.
 * @param level 파괴된 레벨
 * @returns 드랍 결과 (드랍 없으면 null)
 */
export function calcDestroyDrop(level: number): DropResult | null {
    const def = getMaterialDefForLevel(level);
    if (!def) return null;
    const amount = randInt(def.dropOnDestroy[0], def.dropOnDestroy[1]);
    return { key: def.key, amount };
}

/**
 * Materials 객체에 드랍 결과를 적용하여 새 Materials 반환 (불변).
 */
export function applyDrop(materials: Materials, drop: DropResult, multiplier: number = 1): Materials {
    const added = Math.max(1, Math.round(drop.amount * multiplier));
    return { ...materials, [drop.key]: materials[drop.key] + added };
}

/**
 * 재료 총 개수
 */
export function totalMaterials(m: Materials): number {
    return m.iron_dust + m.refining_core + m.lava_essence + m.abyss_crystal;
}
